<template>
  <div data-testid="tasklist__add-task-root">
    <v-menu
      v-if="!hideButton"
      offset-y
      :nudge-bottom="6"
      content-class="tasklist-add-task__menu"
      transition="fade-transition"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          v-bind="attrs"
          v-on="on"
          color="primaryBtn"
          data-testid="tasklist__add-btn"
        >
          <v-icon class="mr-1" data-testid="tasklist__add-icon">mdi-plus</v-icon>
          <span data-testid="tasklist__add-label">{{ buttonLabel }}</span>
          <v-icon class="ml-1" small>mdi-chevron-down</v-icon>
        </v-btn>
      </template>

      <div class="tasklist-add-task__menu-panel" data-testid="tasklist__add-task-menu">
        <p class="tasklist-add-task__menu-label ma-0" data-testid="tasklist__add-task-menu-label">
          Select type
        </p>
        <button
          type="button"
          class="tasklist-add-task__menu-item"
          data-testid="tasklist__add-ar-btn"
          @click="open(arType)"
        >
          Account Reconciliation
        </button>
        <button
          type="button"
          class="tasklist-add-task__menu-item"
          data-testid="tasklist__add-checklist-btn"
          @click="open(checklistType)"
        >
          Checklist
        </button>
      </div>
    </v-menu>

    <AddTaskDialog
      ref="addTaskDialog"
      :visible.sync="visible"
      :project-id="projectId"
      :custom-columns="customColumns"
      :category-acc="categoryAcc"
      :subcategory-acc="subcategoryAcc"
      :coa-category-acc="coaCategoryAcc"
      :coa-subcategory-acc="coaSubcategoryAcc"
      :location-list="filteredLocations"
      :users="activeUsersList"
      :due-date-min-date="dueDateMinDate"
      :due-date-max-date="dueDateMaxDate"
      :location-header="$store.state.LocationHeader || 'Location'"
      :all-tags="allTags"
      :possible-assertions="possibleAssertions"
      :loading="loading"
      @save="onSave"
    />
  </div>
</template>

<script>
import './TasklistAddTask.scss';
import AddTaskDialog from './drawers/AddTaskDialog/AddTaskDialog.vue';
import { MAX_DUE_DATE_DAYS } from '../../../utils/constants';
import { getCycleDueDateWindow } from '../../../utils/workingDays';

export default {
  name: 'TasklistAddTask',
  components: {
    AddTaskDialog,
  },
  props: {
    projectId: {
      type: String,
      default: '',
    },
    customColumns: {
      type: Array,
      default: () => [],
    },
    categoryAcc: {
      type: Array,
      default: () => [],
    },
    subcategoryAcc: {
      type: Array,
      default: () => [],
    },
    coaCategoryAcc: {
      type: Array,
      default: () => [],
    },
    coaSubcategoryAcc: {
      type: Array,
      default: () => [],
    },
    userDetails: {
      type: Object,
      default: () => ({ activeUsers: [], activeUserIds: [] }),
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
    buttonLabel: {
      type: String,
      default: 'New Task',
    },
  },
  data() {
    return {
      visible: false,
      loading: false,
    };
  },
  computed: {
    possibleAssertions() {
      return this.$constants.TASK_LIST_CONSTANTS.ASSERTIONS || [];
    },
    checklistType() {
      return this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.NORMAL;
    },
    arType() {
      return this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION;
    },
    usersList() {
      return this.$store.getters['tasks/getTaskListData']?.usersList || [];
    },
    activeUsersList() {
      const activeIds = new Set(this.userDetails?.activeUserIds || []);
      return (this.usersList || []).filter((user) => activeIds.has(user._id));
    },
    filteredLocations() {
      const filtered = this.$store.getters['tasks/getFilteredLocations'];
      if (filtered && filtered.length > 0) return filtered;
      return this.$store.getters['tasks/getEnabledLocations'] || [];
    },
    allTags() {
      const masterDefaults = ['Yearly', 'Half-yearly', 'Monthly'];
      const taskListData = this.$store.getters['tasks/getTaskListData']?.checklist || [];
      const fromChecklist = taskListData.flatMap((task) => task.tags || []);
      return [...new Set([...masterDefaults, ...fromChecklist].filter(Boolean))];
    },
    dueDateMinDate() {
      return this.dueDateWindow.minIso;
    },
    dueDateMaxDate() {
      return this.dueDateWindow.maxIso;
    },
    dueDateWindow() {
      const cycleEnd = this.$store.getters['tasks/getTaskListData']?.metadata?.to;
      return getCycleDueDateWindow(cycleEnd, MAX_DUE_DATE_DAYS);
    },
  },
  methods: {
    open(taskType) {
      this.$refs.addTaskDialog?.open(taskType);
    },
    openForEdit(task) {
      this.$refs.addTaskDialog?.openForEdit(task);
    },
    async onSave({ addTaskData, customColumns, selectglsLocal, projectId }) {
      this.loading = true;
      try {
        const result = await this.$store.dispatch('tasks/addEditChecklistItem', {
          addeditdata: { ...addTaskData },
          customColumns,
          selectglsLocal,
          projectId,
        });
        if (result && result.success) {
          if (!result.isEdit && result.createdTask?._id) {
            this.$refs.addTaskDialog?.notifyTaskCreated({
              task: { ...addTaskData, ...result.createdTask, project: projectId },
              projectId,
              userType: this.$route?.query?.userType,
            });
          } else {
            this.$store.commit(
              'sbSuccess',
              result.isEdit ? 'Task updated successfully' : 'Tasklist item added',
            );
          }
          this.visible = false;
          this.$emit('reload');
        } else if (result && result.data && result.data.index0) {
          this.$refs.addTaskDialog?.setServerErrors(result.data.index0);
        } else {
          this.$store.commit('sbError', result?.errorMessage || 'Unknown error!');
        }
      } catch (err) {
        this.$store.commit('sbError', err.message || err || 'Unknown error!');
        console.error('[TasklistAddTask]: onSave error', err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
